import { createFileRoute } from "@tanstack/react-router";
import { Section } from "@/components/layout";
import ogImageAsset from "@/assets/og-tb-logo-navy.png.asset.json";

const ogImage = `https://tablesandbridges.com${ogImageAsset.url}`;

const TITLE = "Terms of Use | Tables and Bridges";
const DESC =
  "Terms governing use of the Tables and Bridges website, materials, and community resources.";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: "https://tablesandbridges.com/terms" },
      { property: "og:type", content: "website" },
      { property: "og:image", content: ogImage },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
      { name: "twitter:image", content: ogImage },
    ],
    links: [{ rel: "canonical", href: "https://tablesandbridges.com/terms" }],
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <div>
      <section className="bg-navy text-warm-white">
        <div className="container-editorial py-20 md:py-28">
          <p className="eyebrow text-gold mb-6">Fine print</p>
          <h1 className="font-display text-4xl md:text-6xl leading-[1.05] tracking-tight max-w-3xl">
            Terms of Use
          </h1>
          <p className="mt-6 text-warm-white/75 max-w-2xl">
            This site is being prepared for launch. Full legal terms will be posted before public
            release. The summary below describes our intended terms of use.
          </p>
        </div>
      </section>

      <Section>
        <div className="container-editorial max-w-3xl space-y-10 text-soft-black/85 leading-relaxed">
          <Block title="Acceptance">
            By accessing or using this website, you agree to these Terms of Use. If you do not
            agree, please do not use the site.
          </Block>
          <Block title="Intellectual property">
            All content, methodology names, frameworks, written materials, and visual design on
            this site are the property of Tables and Bridges and its founder unless otherwise
            noted. You may not copy, reproduce, republish, or redistribute materials without
            prior written permission.
          </Block>
          <Block title="Professional use">
            Frameworks and methodology references on this site are provided for educational and
            professional development purposes. They do not constitute legal advice, agency
            policy, or an operational directive. Agencies remain responsible for their own
            policies, training decisions, and community engagement practices.
          </Block>
          <Block title="Forms and submissions">
            Information submitted through inquiry, application, waitlist, or booking forms is
            used to evaluate fit and coordinate follow-up. Submission does not create a
            contractual relationship. Engagements are governed by separate written agreements.
          </Block>
          <Block title="External links">
            This site may link to third-party resources. We are not responsible for the content,
            privacy practices, or availability of external sites.
          </Block>
          <Block title="Changes">
            We may update these Terms of Use at any time. Continued use of the site after changes
            constitutes acceptance of the updated terms.
          </Block>
          <Block title="Contact">
            Questions about these terms may be sent through the{" "}
            <a href="/book-carlai" className="text-navy underline hover:text-gold">
              contact form
            </a>
            .
          </Block>
          <p className="text-sm text-soft-black/60">Last updated: 2026</p>
        </div>
      </Section>
    </div>
  );
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="font-display text-2xl md:text-3xl text-navy mb-3">{title}</h2>
      <div>{children}</div>
    </div>
  );
}
